import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import RefreshIcon from "@mui/icons-material/Refresh";
import toast from "react-hot-toast";
import usePartnersWS from "../../hooks/usePartnersWS";
import { axiosPartners } from "../../axios/axiosConfig";
import { partnersWSManager } from "../../services/partnersWSManager";

// ─── Change these if API field names ever change ─────────────────────────────
const FIELD_MAP = {
  id:            "id",
  symbolName:    "symbol_name",    // used for WS subscription
  broker:        "broker_name",
  clientId:      "client_id",
  qty:           "quantity",       // used for P&L calculation
  tradeType:     "entry_signal",   // "Buy" | "Sell" — used for P&L calculation
  entryPrice:    "triggered_price",// used for P&L calculation
  staticLtp:     "ltp",            // DB LTP — fallback before WS connects
  tradingSymbol: "trading_symbol",
  date:          "triggered_date", // falls back to "created_at"
};

const f = (trade, key) => trade[FIELD_MAP[key]];
// ─────────────────────────────────────────────────────────────────────────────

const pnlTextColor = (val) =>
  val === null ? "#94a3b8" : val >= 0 ? "#16a34a" : "#dc2626";

// ─── Per-row component — own WS subscription ────────────────────────────────
const TradeRow = ({ trade, index, onPnlChange, onExit }) => {
  const tradeType = f(trade, "tradeType")?.toLowerCase() === "buy" ? "BUY" : "SELL";

  const { ltp, pnl, pnlPercent } = usePartnersWS({
    symbol:     f(trade, "symbolName"),
    entryPrice: f(trade, "entryPrice"),
    quantity:   f(trade, "qty"),
    tradeType,
  });

  useEffect(() => {
    if (pnl !== null) onPnlChange(f(trade, "id"), pnl);
  }, [pnl, trade, onPnlChange]);

  const isBuy      = tradeType === "BUY";
  const displayLtp = ltp ?? parseFloat(f(trade, "staticLtp") ?? 0);
  const isLive     = ltp !== null;
  const color      = pnlTextColor(pnl);

  return (
    <TableRow sx={{ "&:hover": { background: "#f8fafc" }, transition: "background 0.15s" }}>

      <TableCell sx={{ fontSize: 13, color: "#94a3b8", py: 1.5 }}>{index + 1}</TableCell>

      <TableCell sx={{ py: 1.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#04343a", lineHeight: 1.3 }}>
          {f(trade, "symbolName")}
        </Typography>
        <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
          {f(trade, "tradingSymbol")}
        </Typography>
      </TableCell>

      <TableCell sx={{ fontSize: 13, color: "#374151", py: 1.5 }}>
        {f(trade, "broker")}
      </TableCell>

      <TableCell sx={{ fontSize: 13, color: "#374151", py: 1.5, fontFamily: "monospace" }}>
        {f(trade, "clientId")}
      </TableCell>

      <TableCell sx={{ py: 1.5 }}>
        <Chip
          label={f(trade, "tradeType")}
          size="small"
          sx={{
            background: isBuy ? "#dcfce7" : "#fee2e2",
            color:      isBuy ? "#16a34a" : "#dc2626",
            fontWeight: 700,
            fontSize:   11,
            height:     22,
            borderRadius: "6px",
          }}
        />
      </TableCell>

      <TableCell sx={{ fontSize: 13, fontWeight: 600, color: "#374151", py: 1.5 }}>
        ₹{parseFloat(f(trade, "entryPrice")).toFixed(2)}
      </TableCell>

      {/* Live LTP from WebSocket */}
      <TableCell sx={{ py: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#016551" }}>
            ₹{displayLtp.toFixed(2)}
          </Typography>
          {isLive && (
            <Box
              sx={{
                width: 6, height: 6, borderRadius: "50%",
                background: "#16a34a",
                animation: "livePulse 1.5s infinite",
                "@keyframes livePulse": {
                  "0%,100%": { opacity: 1 },
                  "50%":     { opacity: 0.25 },
                },
              }}
            />
          )}
        </Box>
      </TableCell>

      <TableCell sx={{ fontSize: 13, color: "#374151", py: 1.5 }}>
        {f(trade, "qty")}
      </TableCell>

      {/* Live P&L */}
      <TableCell sx={{ py: 1.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color }}>
          {pnl !== null
            ? `${pnl >= 0 ? "+" : "-"}₹${Math.abs(pnl).toFixed(2)}`
            : "—"}
        </Typography>
      </TableCell>

      <TableCell sx={{ py: 1.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color }}>
          {pnlPercent !== null
            ? `${pnlPercent >= 0 ? "+" : ""}${pnlPercent.toFixed(2)}%`
            : "—"}
        </Typography>
      </TableCell>

      <TableCell sx={{ fontSize: 12, color: "#94a3b8", py: 1.5 }}>
        {new Date(
          f(trade, "date") || trade.created_at
        ).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" })}
      </TableCell>

      <TableCell sx={{ py: 1.5 }}>
        <Button
          size="small"
          variant="contained"
          onClick={() => onExit(trade)}
          sx={{
            background: "#dc2626",
            color: "#fff",
            fontWeight: 700,
            fontSize: 11,
            borderRadius: "6px",
            textTransform: "none",
            px: 1.5,
            py: 0.4,
            minWidth: 0,
            "&:hover": { background: "#b91c1c" },
          }}
        >
          Exit
        </Button>
      </TableCell>

    </TableRow>
  );
};

// ─── Main page ───────────────────────────────────────────────────────────────
const OpenTradesPage = () => {
  const [trades,       setTrades]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [wsConnected,  setWsConnected]  = useState(false);
  const [totalPnl,     setTotalPnl]     = useState(null);
  const [exitTrade,    setExitTrade]    = useState(null);
  const [exitLoading,  setExitLoading]  = useState(false);
  const pnlRef = useRef({});

  console.log(trades, "trades")

  const fetchTrades = useCallback(async () => {
    setLoading(true);
    pnlRef.current = {};
    setTotalPnl(null);
    try {
      const res = await axiosPartners.get("/brokers/get-open-trades-for-ra/");
      const data = res.data?.data ?? [];
      setTrades(data);
      const symbols = [...new Set(data.map((t) => t[FIELD_MAP.symbolName]).filter(Boolean))];
      partnersWSManager.bulkSubscribe(symbols);
    } catch (err) {
      console.error("[OpenTrades] fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTrades(); }, [fetchTrades]);

  // WS connection status
  useEffect(() => {
    const unsub = partnersWSManager.subscribeStatus(setWsConnected);
    return unsub;
  }, []);

  const handleExitConfirm = async () => {
    setExitLoading(true);
    try {
      await axiosPartners.post(`/brokers/exit-stock-order-by-ra/${f(exitTrade, "id")}/`);
      toast.success("Trade exited successfully");
      setTrades((prev) => prev.filter((t) => f(t, "id") !== f(exitTrade, "id")));
      delete pnlRef.current[f(exitTrade, "id")];
      setExitTrade(null);
    } catch (e) {
      toast.error(
        e.response?.data?.message || e.response?.data?.detail || e.message || "Failed to exit trade"
      );
    } finally {
      setExitLoading(false);
    }
  };

  // Aggregate total live P&L from all rows
  const handlePnlChange = useCallback((id, pnl) => {
    pnlRef.current[id] = pnl;
    const total = Object.values(pnlRef.current).reduce((s, v) => s + v, 0);
    setTotalPnl(parseFloat(total.toFixed(2)));
  }, []);

  const totalInvested = trades.reduce(
    (sum, t) => sum + parseFloat(f(t, "entryPrice")) * f(t, "qty"), 0
  );

  const totalColor = pnlTextColor(totalPnl);

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>

      {/* ── Page Header ──────────────────────────────────── */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          px: 3, py: 2, mb: 3,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <ShowChartIcon sx={{ color: "#eef2f7", fontSize: 26 }} />
          <Box>
            <Typography sx={{ color: "#ffffff", fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>
              Open Trades
            </Typography>
            <Typography sx={{ color: "#eef2f7", fontSize: 12 }}>
              Live P&amp;L tracking via WebSocket
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* WS status dot */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <Box
              sx={{
                width: 8, height: 8, borderRadius: "50%",
                background: wsConnected ? "#4ade80" : "#f87171",
                animation: wsConnected ? "livePulse 1.5s infinite" : "none",
                "@keyframes livePulse": {
                  "0%,100%": { opacity: 1 },
                  "50%":     { opacity: 0.25 },
                },
              }}
            />
            <Typography sx={{ color: "#eef2f7", fontSize: 12 }}>
              {wsConnected ? "Live" : "Connecting…"}
            </Typography>
          </Box>

          <IconButton
            onClick={fetchTrades}
            size="small"
            sx={{ color: "#eef2f7", "&:hover": { background: "rgba(255,255,255,0.1)" } }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* ── Summary Cards ────────────────────────────────── */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        {[
          { label: "Total Trades",   value: trades.length,              prefix: "",  color: "#04343a" },
          { label: "Total Invested", value: totalInvested.toFixed(2),   prefix: "₹", color: "#016551" },
          {
            label: "Live P&L",
            value: totalPnl !== null
              ? `${totalPnl >= 0 ? "+" : "-"}₹${Math.abs(totalPnl).toFixed(2)}`
              : "—",
            prefix: "",
            color: totalColor,
          },
        ].map((card) => (
          <Box
            key={card.label}
            sx={{
              background: "#ffffff",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
              px: 3, py: 2,
              minWidth: 160, flex: 1,
            }}
          >
            <Typography sx={{ fontSize: 12, color: "#94a3b8", fontWeight: 500, mb: 0.5 }}>
              {card.label}
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: card.color }}>
              {card.prefix}{card.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* ── Table ────────────────────────────────────────── */}
      <Box
        sx={{
          background: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          overflow: "hidden",
        }}
      >
        <Box sx={{ background: "#04343a", px: 3, py: 1.5 }}>
          <Typography sx={{ color: "#ffffff", fontWeight: 600, fontSize: 14 }}>
            Active Positions
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: "#016551" }} size={36} />
          </Box>
        ) : trades.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>
              No open trades found
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "#f8fafc" }}>
                  {["#", "Symbol", "Broker", "Client ID", "Type", "Entry Price", "Live LTP", "Qty", "P&L (₹)", "P&L %", "Date", "Action"].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontSize: 12, fontWeight: 700, color: "#64748b",
                        py: 1.5, borderBottom: "2px solid #e2e8f0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {trades.map((trade, i) => (
                  <TradeRow
                    key={f(trade, "id")}
                    trade={trade}
                    index={i}
                    onPnlChange={handlePnlChange}
                    onExit={setExitTrade}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Exit Confirm Dialog */}
      <Dialog
        open={!!exitTrade}
        onClose={() => !exitLoading && setExitTrade(null)}
        PaperProps={{ sx: { borderRadius: "14px", minWidth: 360 } }}
      >
        <Box sx={{ px: 3, py: 2, background: "#04343a" }}>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>Exit Trade</Typography>
        </Box>
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography sx={{ fontSize: 13, color: "#64748b" }}>
            Are you sure you want to exit the trade for{" "}
            <strong style={{ color: "#0f172a" }}>{exitTrade ? f(exitTrade, "symbolName") : ""}</strong>?
            This action cannot be undone.
          </Typography>
          {exitTrade && (
            <Box sx={{ mt: 1.5, display: "flex", gap: 2 }}>
              <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                Client: <strong style={{ color: "#374151" }}>{f(exitTrade, "clientId")}</strong>
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                Qty: <strong style={{ color: "#374151" }}>{f(exitTrade, "qty")}</strong>
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setExitTrade(null)}
            size="small"
            disabled={exitLoading}
            sx={{ borderRadius: "8px", textTransform: "none", color: "#64748b" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleExitConfirm}
            disabled={exitLoading}
            startIcon={exitLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
            sx={{
              borderRadius: "8px",
              background: "#dc2626",
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              "&:hover": { background: "#b91c1c" },
              "&:disabled": { background: "#94a3b8", boxShadow: "none" },
            }}
          >
            {exitLoading ? "Exiting…" : "Confirm Exit"}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default OpenTradesPage;
